/**
 * HandoffSuccessPage
 * Terminal confirmation after a claim, a release, or a decline.
 *
 * The four states used to be four near-identical full-page blocks with
 * different gradients; they are now one table of copy rendered through the
 * shared HandoffOutcome card.
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Flame, Sparkles, User, XCircle } from 'lucide-react';
import {
  HandoffAction,
  HandoffOutcome,
  HandoffPageShell,
  HandoffTone,
} from '../../components/features/handoff/HandoffPageShell';

interface OutcomeCopy {
  tone: HandoffTone;
  icon: React.ReactNode;
  title: string;
  message: string;
}

const OUTCOMES: Record<string, OutcomeCopy> = {
  claimed: {
    tone: 'emerald',
    icon: <CheckCircle />,
    title: 'Lead claimed',
    message:
      'This lead is yours. You are now responsible for all future communication with this contact.',
  },
  declined: {
    tone: 'amber',
    icon: <XCircle />,
    title: 'Handoff declined',
    message:
      'You have declined this handoff. The lead will be routed to the next available agent or returned to Remi for automated follow-up.',
  },
  released: {
    tone: 'blue',
    icon: <Sparkles />,
    title: 'Lead released',
    message:
      'Your notes have been saved and the lead has been returned to Remi for automated follow-up.',
  },
};

const DEFAULT_OUTCOME: OutcomeCopy = {
  tone: 'emerald',
  icon: <CheckCircle />,
  title: 'All set',
  message: 'Your action has been processed and the lead has been updated.',
};

const HandoffSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const contactId = searchParams.get('contactId');

  const outcome = (type && OUTCOMES[type]) || DEFAULT_OUTCOME;

  // After a claim, viewing the contact is the natural next step, so it leads and
  // the queue is the secondary. Every other outcome is terminal for that lead,
  // so those offer the queue and the contact list instead.
  const isClaimedWithContact = type === 'claimed' && Boolean(contactId);
  const viewQueue: HandoffAction = {
    label: 'See other hot leads',
    onClick: () => navigate('/dashboard?openHandoffQueue=true'),
    icon: <Flame className="mr-2 h-4 w-4" />,
    variant: isClaimedWithContact ? 'outline' : 'primary',
  };

  const actions: HandoffAction[] = isClaimedWithContact
    ? [
        {
          label: 'View contact',
          onClick: () => navigate(`/contact/${contactId}`),
          icon: <User className="mr-2 h-4 w-4" />,
          variant: 'primary',
        },
        viewQueue,
      ]
    : [
        viewQueue,
        {
          label: 'Go to contacts',
          onClick: () => navigate('/contacts'),
          variant: 'outline',
        },
      ];

  return (
    <HandoffPageShell>
      <HandoffOutcome
        tone={outcome.tone}
        icon={outcome.icon}
        title={outcome.title}
        message={outcome.message}
        actions={actions}
      />
    </HandoffPageShell>
  );
};

export default HandoffSuccessPage;
